import React, { useState, useEffect } from 'react';
import styles from './styles.module.scss';
import Card from '../../common/cards/Card';
import { useHistory } from 'react-router-dom';
import { routes } from '../../../assets/routes';
import { toast } from 'react-toastify';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  getMoodReasons,
  getOrganizationFeaturesRequest,
  sendMoodRequest
} from '../../../helpers/api';
import { Alert, TextField } from '@mui/material';
import Button from '@mui/material/Button';
import classNames from 'classnames';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import mood5 from '../../../assets/icons/moodTracker/mood5.svg';
import mood4 from '../../../assets/icons/moodTracker/mood4.svg';
import mood3 from '../../../assets/icons/moodTracker/mood3.svg';
import mood2 from '../../../assets/icons/moodTracker/mood2.svg';
import mood1 from '../../../assets/icons/moodTracker/mood1.svg';
import { useTranslation } from '../../../i18n/LanguageProvider';

const AddMoodWithReason = () => {
  const { t } = useTranslation();
  const moods = [
    { value: 5, src: mood5 },
    { value: 4, src: mood4 },
    { value: 3, src: mood3 },
    { value: 2, src: mood2 },
    { value: 1, src: mood1 }
  ];
  const history = useHistory();
  const [selectedMood, setSelectedMood] = useState(null);
  const [moodComment, setMoodComment] = useState(null);
  const [anonymous, setAnonymous] = useState(true);
  const [selectedReason, setSelectedReason] = useState(null);
  const [reasons, setReasons] = useState([]);
  const randomExample = (group) => {
    const sentences = t(`mood.examples.${group}`);
    return (
      t('mood.examplePrefix') +
      sentences[Math.floor(Math.random() * sentences.length)]
    );
  };
  const [placeHolder, setPlaceHolder] = useState(() =>
    randomExample('neutral')
  );

  useEffect(() => {
    let group = 'neutral';
    if (selectedMood === 1 || selectedMood === 2) {
      group = 'bad';
    } else if (selectedMood === 4 || selectedMood === 5) {
      group = 'good';
    }
    setPlaceHolder(randomExample(group));
  }, [selectedMood]);

  const onClickOnMoodIcon = (moodIndex) => {
    setSelectedMood(moodIndex);
  };
  const handleMoodCommentChange = (e) => {
    setMoodComment(e.target.value);
  };
  const sendMoodMutation = useMutation(sendMoodRequest, {
    onSuccess() {
      toast.success(t('mood.submittedGreeting'));
      history.push(routes.HOME);
    },
    onError(e) {
      toast.error(e.response?.data.detail || t('mood.submitError'));
    }
  });
  const getMoodReasonMutation = useMutation(getMoodReasons, {
    onSuccess(response) {
      setReasons(response.map((moodReason) => moodReason.reason));
    },
    onError(e) {
      toast.error(e.response?.data.detail || t('mood.reasonsError'));
    }
  });

  useEffect(() => {
    getMoodReasonMutation.mutate();
  }, []);

  const { data: orgFeatures } = useQuery(
    ['organizationFeatures'],
    getOrganizationFeaturesRequest
  );

  const defaultAnonymos = orgFeatures?.data?.find(
    (feature) => feature.feature_name === 'secret_mood'
  )?.active;

  const secretPropmt = defaultAnonymos
    ? t('mood.showNameToHr')
    : t('mood.hideNameFromHr');

  const skipThisStep = () => {
    history.push(routes.HOME);
  };

  return (
    <div className={styles.currentMood}>
      <div
        className={classNames(
          styles.moodWrapper,
          !selectedMood && styles.expanded
        )}
      >
        <div className={styles.label}>
          <div className={styles.question}>
            <div>{t('mood.hello')}</div>
            <div>{t('mood.nowQuestion')}</div>
          </div>

          <Button
            variant="outlined"
            size="small"
            onClick={() => history.push(routes.HOME)}
          >
            {t('common.skip')}
          </Button>
        </div>

        <Card extraClass={styles.emojisBox} noPointer>
          {moods.map((mood) => (
            <Card
              key={mood.value}
              selected={selectedMood === mood.value}
              extraClass={styles.card}
            >
              <img
                alt={'mood' + mood.value.toString()}
                src={mood.src}
                onClick={() => {
                  onClickOnMoodIcon(mood.value);
                }}
              />
            </Card>
          ))}
        </Card>
      </div>
      <div
        className={classNames(
          styles.descriptionWrapper,
          selectedMood && styles.show
        )}
      >
        <div className={styles.reasonsBox}>
          {reasons.map((reason) => (
            <Card
              extraClass={classNames(
                styles.reasonCard,
                selectedReason === reason && styles.selected
              )}
              key={reason}
              selected={selectedReason === reason}
              onClick={() => {
                if (selectedReason === reason) {
                  setSelectedReason(null);
                  return;
                }
                setSelectedReason(reason);
              }}
            >
              <span>{reason}</span>
            </Card>
          ))}
        </div>
        <div className={styles.noteBox}>
          <TextField
            id="give-kudos"
            name="give-kudos"
            multiline
            hiddenLabel
            className={styles.note}
            value={moodComment}
            onChange={handleMoodCommentChange}
            fullWidth
            placeholder={placeHolder}
          />
          {anonymous && defaultAnonymos && (
            <Alert variant="outlined" severity="info" sx={{ mt: 1 }}>
              {t('mood.anonymousHint')}
            </Alert>
          )}
          {defaultAnonymos && (
            <FormControlLabel
              label={secretPropmt}
              control={
                <Checkbox
                  checked={!anonymous}
                  onChange={() => setAnonymous(!anonymous)}
                />
              }
            />
          )}
        </div>
        <Button
          variant="contained"
          disabled={!selectedMood}
          loading={sendMoodMutation.isLoading}
          onClick={() =>
            sendMoodMutation.mutate({
              mood: selectedMood,
              comment: moodComment,
              reason: selectedReason,
              anonymous: defaultAnonymos ? anonymous : !anonymous
            })
          }
          className={styles.sendButton}
          fullWidth
        >
          <span>{t('common.submit')}</span>
        </Button>
        <Button
          variant="outlined"
          fullWidth
          onClick={skipThisStep}
          className={styles.skipButton}
        >
          <span>{t('mood.later')}</span>
        </Button>
      </div>
    </div>
  );
};

export default AddMoodWithReason;
